import React, { useEffect, useRef } from 'react';

export const About: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            entry.target.classList.remove('animate-out');
          } else {
            entry.target.classList.remove('animate-in');
            entry.target.classList.add('animate-out');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = contentRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => {
      elements?.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="about-container" ref={contentRef}>
      <div className="about-content">
        <div className="animate-on-scroll">
          <h1>
              Skill Trees is a tool for creating and sharing skill trees.
          </h1>
          <p>
              It lets you quickly build and share guides for getting good at things using 
              the "skill tree" format that is popular in video games.
          </p>
          <figure>
            <img 
              src="/images/about/tree-screenshot.png" 
              alt="A skill tree for Tekken 8" 
            />
            <figcaption>A skill tree for Tekken 8</figcaption>
          </figure>
        </div>

        <div className="animate-on-scroll">
          <h2>
              Why A Skill Tree?
          </h2>
          <p>
              Video games have long used a tree of skills to visualize skill progression because players find it easy to understand. 
          </p>
          <figure>
            <img 
              src="/images/about/cyberpunk-skill-tree-example.png" 
              alt="An example of a skill tree from the video game, Cyberpunk 2077" 
            />
            <figcaption>An example of a skill tree from the video game, Cyberpunk 2077</figcaption>
          </figure>
          <p>
              Not only does this work great in video games, it works great in the real world too! Large skills 
              such as coding can be broken up into component skills, and these have prerequisites 
              that lead into them, and this is a skill tree! Even university courses are often organized
              this way, with prerequisites for each course.
          </p>
          <figure>
            <img 
              src="/images/about/programming-tree.png" 
              alt="An example of a skill tree from a programming course"
            />
            <figcaption>An example of a skill tree from a programming course</figcaption>
          </figure>
        </div>

        <div className="animate-on-scroll">
          <h2>
            Using a Skill Tree
          </h2>
          <p>
            Users can navigate the tree like they would in a video game, check out the guide for each skill leaf, follow links to prerequisites and next steps, and more!
          </p>
          <figure>
            <img
              src="/images/about/tree-with-panel-screenshot.png" 
                alt="A user has selected a skill leaf and is reading the guide for it"
              />
            <figcaption>A user has selected a skill leaf and is reading the guide for it</figcaption>
          </figure>
        </div>

        <div className="animate-on-scroll">
          <h2>
            Creating a Skill Tree
          </h2>
          <p>
            Skill Tree creators can create and share their trees with others by dragging skill leaves into the tree and linking them together.
            Creators can edit the markdown guide for each skill leaf by selecting it in the tree. Guides support linking to other leaves with <code>[label](leaf:short-id)</code> syntax, section links, and more. Upcoming features will 
            include the ability to embed videos, add special attributes, and much more!
          </p>
          <figure>
            <img
              src="/images/about/full-editor-screenshot.png" 
              alt="A creator is editing a skill tree"
            />
            <figcaption>A creator is editing a skill tree</figcaption>
          </figure>
        </div>

        <div className="animate-on-scroll">
          <h2>
            Get Access to Skill Trees
          </h2>
          <p>
            Follow the development process and get early full access to the app by joining our <a href="https://patreon.com/SkillTreesGG?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink" target="_blank" rel="noopener noreferrer">Patreon</a>! 
          </p>
          <p>
            Members get early access to the full app, including the ability to create and share skill trees, updates on the development process, access to the feature roadmap, and more!
          </p>
          <a href="https://patreon.com/SkillTreesGG" target="_blank" rel="noopener noreferrer">
            <img
              src="/images/about/patreon-card.png" 
              alt="Patreon"
            />
          </a>
        </div>
      </div>
    </div>
  );
};
